import type { Graph, Cell } from '@maxgraph/core';
import type { GroupSpec } from './types';
import { generateId } from '../utils/id';
import { CellNotFoundError } from './errors';

const GROUP_PADDING = 20;
const GROUP_START_SIZE = 30; // matches GROUP_STYLE.startSize in styles.ts

// ── collectMemberCells ────────────────────────────────────────────────────────

/**
 * Collect the cells that should live inside a group's swimlane for the given
 * stepIds (operation IDs). Mirrors the topology rule in
 * `projection/groupMembership.ts#getGroupInternalMaterials`: a material is a
 * member when it is connected — as input OR output — to any operation in the
 * group. Boundary materials (produced by a group op but later consumed
 * outside, or feeding a group op from outside) are INCLUDED; maxGraph draws
 * cross-boundary edges to the swimlane border when it collapses.
 *
 * Two op renderings must both be handled:
 *   - Simple ops (TRANSFORM/TRANSFER/MEASURE) render as edges whose value
 *     carries `operationNodeId`. Both terminals are member materials.
 *   - COMPLEX ops render as vertices (cell id === opId). The vertex itself is
 *     a member, and every material connected to it by an edge is a member.
 */
export function collectMemberCells(graph: Graph, stepIds: string[]): Cell[] {
  const model = graph.getDataModel();
  const groupOpIds = new Set(stepIds);
  const memberIds = new Set<string>();

  // Pass 1: op-edges (simple ops) — collect both terminal materials.
  const allEdges = graph.getChildCells(graph.getDefaultParent(), false, true);
  for (const edge of allEdges) {
    const opId = edge.getValue()?.operationNodeId;
    if (!opId || !groupOpIds.has(opId)) continue;
    const src = edge.getTerminal(true)?.getId();
    const tgt = edge.getTerminal(false)?.getId();
    if (src) memberIds.add(src);
    if (tgt) memberIds.add(tgt);
  }

  // Pass 2: COMPLEX op vertices — the vertex plus every connected material.
  for (const opId of stepIds) {
    const opCell = model.getCell(opId);
    if (!opCell || !opCell.isVertex()) continue;
    memberIds.add(opId);
    const connected = [
      ...graph.getIncomingEdges(opCell, null),
      ...graph.getOutgoingEdges(opCell, null),
    ];
    for (const e of connected) {
      const src = e.getTerminal(true)?.getId();
      const tgt = e.getTerminal(false)?.getId();
      if (src && src !== opId) memberIds.add(src);
      if (tgt && tgt !== opId) memberIds.add(tgt);
    }
  }

  const result: Cell[] = [];
  for (const id of memberIds) {
    const cell = model.getCell(id);
    if (cell) result.push(cell);
  }
  return result;
}

// ── addGroup ──────────────────────────────────────────────────────────────────

export function addGroup(graph: Graph, spec: GroupSpec): string {
  const id = spec.id ?? generateId('group');

  graph.batchUpdate(() => {
    const memberCells = collectMemberCells(graph, spec.stepIds);

    // _pendingGroupSpec is a private field on WorkflowGraph that the
    // createGroupCell override reads. We set it before calling groupCells()
    // (synchronous) and clear it immediately after.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (graph as any)._pendingGroupSpec = { id, label: spec.label };
      // groupCells accepts null at runtime (triggers createGroupCell override)
      // but the TypeScript types require Cell — cast to satisfy the compiler.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const groupCell = graph.groupCells(null as any, GROUP_PADDING, memberCells);

      if (!groupCell) return;

      const geo = groupCell.getGeometry()!;
      graph.updateAlternateBounds(groupCell, geo, true);
      graph.getDataModel().setGeometry(groupCell, geo);
    } finally {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (graph as any)._pendingGroupSpec = null;
    }
  });

  return id;
}

// ── updateGroup ──────────────────────────────────────────────────────────────

export function updateGroup(
  graph: Graph,
  id: string,
  patch: Partial<Pick<GroupSpec, 'label' | 'position' | 'size'>>,
): void {
  const cell = graph.getDataModel().getCell(id);
  if (!cell) throw new CellNotFoundError(id);

  graph.batchUpdate(() => {
    if (patch.label !== undefined) {
      const val = cell.getValue();
      graph.getDataModel().setValue(cell, { ...val, label: patch.label });
    }
    if (patch.position || patch.size) {
      const geo = cell.getGeometry()?.clone();
      if (geo) {
        if (patch.position) {
          geo.x = patch.position.x;
          geo.y = patch.position.y;
        }
        if (patch.size) {
          geo.width = patch.size.width;
          geo.height = patch.size.height;
        }
        graph.getDataModel().setGeometry(cell, geo);
      }
    }
  });
}

// ── removeGroup ──────────────────────────────────────────────────────────────

export function removeGroup(
  graph: Graph,
  id: string,
  options?: { cascadeSteps?: boolean },
): void {
  const model = graph.getDataModel();
  const groupCell = model.getCell(id);
  if (!groupCell) return;

  graph.batchUpdate(() => {
    if (options?.cascadeSteps) {
      const children: Cell[] = [];
      for (let i = 0; i < groupCell.getChildCount(); i++) {
        children.push(groupCell.getChildAt(i));
      }
      if (children.length > 0) {
        for (const child of children) model.remove(child);
      }
      model.remove(groupCell);
    } else {
      // ungroupCells: promotes children to defaultParent with correct
      // coordinate translation, then removes the swimlane shell
      graph.ungroupCells([groupCell]);
    }
  });
}

// ── foldGroup ────────────────────────────────────────────────────────────────

export function foldGroup(
  graph: Graph,
  groupIds: string | string[],
  collapse: boolean,
): void {
  const ids = Array.isArray(groupIds) ? groupIds : [groupIds];
  const model = graph.getDataModel();
  const groupCells: Cell[] = [];

  for (const groupId of ids) {
    const groupCell = model.getCell(groupId);
    if (groupCell) {
      groupCells.push(groupCell);
    }
  }

  if (groupCells.length === 0) return;

  graph.batchUpdate(() => {
    graph.foldCells(collapse, false, groupCells);
    // foldCells handles: geometry swap, hide/show children, edge promotion
    // LayoutManager fires after batchUpdate
  });
}

// ── addStepsToGroup ──────────────────────────────────────────────────────────

export function addStepsToGroup(
  graph: Graph,
  groupId: string,
  stepIds: string[],
  _insertAt?: number,
): void {
  const model = graph.getDataModel();
  const groupCell = model.getCell(groupId);
  if (!groupCell) return;

  graph.batchUpdate(() => {
    const newCells = collectMemberCells(graph, stepIds).filter(
      (cell) => cell.getParent() !== groupCell,
    );

    if (newCells.length > 0) {
      // absolute: true preserves absolute position during reparenting
      graph.addCells(newCells, groupCell, null, null, null, true);
    }

    graph.updateGroupBounds(
      [groupCell],
      GROUP_PADDING,
      true,
      GROUP_PADDING + GROUP_START_SIZE,
    );
    const geo = groupCell.getGeometry()!;
    graph.updateAlternateBounds(groupCell, geo, true);
    model.setGeometry(groupCell, geo);
  });
}

// ── removeStepsFromGroup ─────────────────────────────────────────────────────

export function removeStepsFromGroup(
  graph: Graph,
  groupId: string,
  stepIds: string[],
): void {
  const model = graph.getDataModel();
  const groupCell = model.getCell(groupId);
  if (!groupCell) return;

  graph.batchUpdate(() => {
    const cellsToPromote = collectMemberCells(graph, stepIds).filter(
      (cell) => cell.getParent() === groupCell,
    );

    if (cellsToPromote.length > 0) {
      // removeCellsFromParent promotes to grandparent with coordinate translation
      graph.removeCellsFromParent(cellsToPromote);
    }

    if (groupCell.getChildCount() > 0) {
      graph.updateGroupBounds(
        [groupCell],
        GROUP_PADDING,
        true,
        GROUP_PADDING + GROUP_START_SIZE,
      );
      const geo = groupCell.getGeometry()!;
      graph.updateAlternateBounds(groupCell, geo, true);
      model.setGeometry(groupCell, geo);
    }
  });
}
